import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AnalyticsPanel } from './AnalyticsPanel';
import { Plus, X, SlidersHorizontal } from 'lucide-react';
import { api } from '@/lib/api';
import { PromptDialog } from '@/components/PromptDialog';
import { WhatIfParamDialog } from './WhatIfParamDialog';

export function AdvancedTab({
  activeVisual, getVizConfig, setVizConfig, transformedData, result,
  rowDims, valueMeasures, allFields, modelId,
  parameters, setParameters, paramValues, setParamValues,
  pageField, setPageField, pageValues, setPageValues, pageIndex, setPageIndex,
  pageAnimating, setPageAnimating,
  highlightField, setHighlightField, highlightValue, setHighlightValue,
  condRules, setCondRules,
}) {
  const [paramPromptOpen, setParamPromptOpen] = useState(false);
  const [whatIfOpen, setWhatIfOpen] = useState(false);

  const handleAddTextParam = (name) => {
    setParameters(prev => [...prev, { name, type: 'text', defaultValue: '' }]);
  };
  const handleAddWhatIf = (cfg) => {
    setParameters(prev => [...prev, { ...cfg, type: 'slider' }]);
    setParamValues(prev => ({ ...prev, [cfg.name]: cfg.defaultValue }));
  };

  return (
    <React.Fragment>
      {/* Analytics */}
      <AnalyticsPanel vizConfig={getVizConfig(activeVisual)} onConfigChange={(updates) => setVizConfig(activeVisual, updates)} data={transformedData || result?.data} rowDims={rowDims} valueMeasures={valueMeasures} allFields={allFields} onPinCorrelation={(corrData) => setVizConfig(activeVisual, { pinnedCorrelation: corrData })} />
      {/* Parameters */}
      <div className="p-3 border-b border-border/30">
        <div className="flex items-center justify-between mb-1">
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Parameters</p>
          <div className="flex gap-0.5">
            <button onClick={() => setParamPromptOpen(true)} className="text-primary text-[8px] hover:text-primary/80" title="Add text parameter" data-testid="add-text-param-btn"><Plus className="w-3 h-3" /></button>
            <button onClick={() => setWhatIfOpen(true)} className="text-indigo-400 text-[8px] hover:text-indigo-300" title="Add what-if slider" data-testid="add-whatif-btn"><SlidersHorizontal className="w-3 h-3" /></button>
          </div>
        </div>
        {parameters.map((param, pi) => (
          <div key={param.name || pi} className="mb-1.5">
            <div className="flex items-center gap-1">
              <span className="text-[8px] text-muted-foreground w-16 truncate" title={param.name}>{param.name}</span>
              <button onClick={() => setParameters(prev => prev.filter((_, i) => i !== pi))} className="text-muted-foreground/30 hover:text-destructive ml-auto"><X className="w-2.5 h-2.5" /></button>
            </div>
            {param.type === 'slider' ? (
              <div className="flex items-center gap-1.5 mt-0.5">
                <input type="range" min={param.min || 0} max={param.max || 100} step={param.step || 1} value={paramValues[param.name] || param.defaultValue || 50} onChange={e => setParamValues(prev => ({ ...prev, [param.name]: Number(e.target.value) }))} className="flex-1 h-1.5 accent-primary cursor-pointer" />
                <span className="text-[9px] font-mono text-primary w-10 text-right">{paramValues[param.name] || param.defaultValue || 50}{param.suffix || ''}</span>
              </div>
            ) : (
              <Input value={paramValues[param.name] || param.defaultValue || ''} onChange={e => setParamValues(prev => ({ ...prev, [param.name]: e.target.value }))} className="!h-6 !text-[9px] rounded bg-muted/30 border-border/30" placeholder="Value" />
            )}
          </div>
        ))}
        {parameters.length === 0 && <p className="text-[8px] text-muted-foreground/30 italic">No parameters</p>}
      </div>
      {/* Pages Shelf */}
      <div className="p-3 border-b border-border/30">
        <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">Pages (Animate)</p>
        <div className="rounded-md border-2 border-dashed border-border/30 min-h-[24px] px-1.5 py-0.5 text-[9px]" onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary/50'); }} onDragLeave={e => e.currentTarget.classList.remove('border-primary/50')} onDrop={async e => { e.preventDefault(); e.currentTarget.classList.remove('border-primary/50'); try { const d = JSON.parse(e.dataTransfer.getData('application/json')); if (d.type === 'dimension') { setPageField(d.name); const res = await api.executeQuery({ model_id: modelId, dimensions: [d.name], measures: [], limit: 100 }); setPageValues([...new Set((res.data || []).map(r => r[d.name]).filter(Boolean))].sort()); setPageIndex(0); } } catch {} }}>
          {pageField ? (
            <div className="flex items-center gap-1">
              <Badge className="text-[8px] bg-emerald-500/15 text-emerald-400 border-emerald-500/20 cursor-pointer" onClick={() => { setPageField(''); setPageValues([]); setPageAnimating(false); }}>{pageField} <X className="w-2 h-2 ml-0.5" /></Badge>
              {pageValues.length > 0 && (
                <div className="flex items-center gap-0.5 ml-auto">
                  <button onClick={() => setPageIndex(p => Math.max(0, p - 1))} className="text-[10px] text-muted-foreground hover:text-foreground px-1">&lt;</button>
                  <span className="text-[8px] text-foreground font-mono">{pageValues[pageIndex]}</span>
                  <button onClick={() => setPageIndex(p => Math.min(pageValues.length - 1, p + 1))} className="text-[10px] text-muted-foreground hover:text-foreground px-1">&gt;</button>
                  <button onClick={() => { if (pageAnimating) { setPageAnimating(false); } else { setPageIndex(0); setPageAnimating(true); } }} className={'text-[8px] px-1.5 py-0.5 rounded ' + (pageAnimating ? 'bg-red-500/20 text-red-400' : 'bg-primary/20 text-primary')}>{pageAnimating ? 'Stop' : 'Play'}</button>
                </div>
              )}
            </div>
          ) : <span className="text-muted-foreground/30">Drag dimension to animate</span>}
        </div>
      </div>
      {/* Highlighter */}
      <div className="p-3 border-b border-border/30">
        <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">Highlighter</p>
        <Select value={highlightField || '_none_'} onValueChange={v => { setHighlightField(v === '_none_' ? null : v); setHighlightValue(null); }}>
          <SelectTrigger className="h-6 text-[9px] rounded bg-muted/30 border-border/30"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_none_">Off</SelectItem>
            {rowDims.map(d => <SelectItem key={d} value={d}>Highlight by {d.replace(/_/g, ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        {highlightField && result?.data?.length > 0 && (() => {
          const uniqueVals = [...new Set(result.data.map(r => r[highlightField]).filter(Boolean))].sort();
          return (
            <div className="flex flex-wrap gap-1 mt-1.5">
              <button onClick={() => setHighlightValue(null)} className={'px-1.5 py-0.5 rounded text-[8px] transition-all ' + (!highlightValue ? 'bg-amber-500/20 text-amber-400' : 'bg-muted/20 text-muted-foreground hover:bg-muted/40')}>All</button>
              {uniqueVals.map(v => (
                <button key={String(v)} onClick={() => setHighlightValue(highlightValue === v ? null : v)} className={'px-1.5 py-0.5 rounded text-[8px] transition-all ' + (highlightValue === v ? 'bg-amber-500/20 text-amber-400 font-semibold' : 'bg-muted/20 text-muted-foreground hover:bg-muted/40')}>{String(v)}</button>
              ))}
            </div>
          );
        })()}
      </div>
      {/* Conditional Rules */}
      <div className="p-3">
        <div className="flex items-center justify-between mb-1"><p className="text-[9px] text-muted-foreground uppercase tracking-wider flex items-center gap-1"><SlidersHorizontal className="w-2.5 h-2.5" /> Rules</p><button onClick={() => setCondRules(p => [...p, { operator: 'gt', threshold: 0, color: '#ef4444' }])}><Plus className="w-3 h-3 text-primary" /></button></div>
        {condRules.map((r, i) => (
          <div key={`rule-${i}-${r.operator}-${r.threshold}`} className="flex items-center gap-1 mb-1">
            <select value={r.operator} onChange={e => setCondRules(p => p.map((x, j) => j === i ? { ...x, operator: e.target.value } : x))} className="h-5 text-[8px] rounded bg-muted/30 border border-border/30 text-foreground px-0.5"><option value="gt">&gt;</option><option value="lt">&lt;</option><option value="gte">&ge;</option></select>
            <Input value={r.threshold} onChange={e => setCondRules(p => p.map((x, j) => j === i ? { ...x, threshold: Number(e.target.value) } : x))} className="!h-6 !text-[9px] rounded bg-muted/30 border-border/30 w-12 px-1" type="number" />
            <input type="color" value={r.color} onChange={e => setCondRules(p => p.map((x, j) => j === i ? { ...x, color: e.target.value } : x))} className="w-5 h-5 rounded cursor-pointer border-0" />
            <button onClick={() => setCondRules(p => p.filter((_, j) => j !== i))}><X className="w-2.5 h-2.5 text-muted-foreground hover:text-destructive" /></button>
          </div>
        ))}
      </div>

      <PromptDialog
        open={paramPromptOpen} onOpenChange={setParamPromptOpen}
        title="New Parameter"
        label="Parameter name"
        placeholder="e.g. Region, Year, Target"
        confirmLabel="Add Parameter"
        onConfirm={handleAddTextParam}
        testId="param-prompt"
      />
      <WhatIfParamDialog open={whatIfOpen} onOpenChange={setWhatIfOpen} onCreate={handleAddWhatIf} />
    </React.Fragment>
  );
}
